import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import { DashboardAuthorization } from "@asd/customer-portal-api";
import { formatShortDate, parseWallClockDate } from "@asd/customer-portal-ui";
import moment from "moment-timezone";
import { useSelector } from "react-redux";

import { getScrambledDateString, useScrambler } from "~/providers/ScramblerProvider";
import { DashboardService } from "~/services";
import { useDashboardStaffProvider } from "../dashboard_staff/DashboardStaffProvider";

type DashboardPPTProviderType = {
  authorizations: DashboardAuthorization[];
  authorizationOptions: any[];
  error: string | null;
  isLoading: boolean;
};

const DashboardPPTContext = createContext<DashboardPPTProviderType>({} as DashboardPPTProviderType);

export const useDashboardPPTProvider = () => useContext(DashboardPPTContext);

export const DashboardPPTProvider: FC<PropsWithChildren> = ({ children }) => {
  const { selectedPPTConfiguration, ...baseConfiguration } = useSelector((state: any) => state.ConfigurationSlice);
  const configuration = selectedPPTConfiguration || baseConfiguration;

  const { isScramblerEnabled } = useScrambler();
  const { selectedParticipant } = useDashboardStaffProvider();

  const [dashboardData, setDashboardData] = useState({
    authorizations: [],
    authorizationOptions: [],
  } as Omit<DashboardPPTProviderType, "error" | "isLoading">);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isInitialized = useRef(false);
  const dataPromise = useRef<Promise<void> | null>(null);
  const dataAbortController = useRef<AbortController>(new AbortController());

  useEffect(() => {
    if (!isInitialized.current) {
      dataAbortController.current?.abort();
      dataAbortController.current = new AbortController();

      setIsLoading(true);
      setError(null);
      const thisPromise = DashboardService.getData(
        moment(configuration.minDate),
        moment(configuration.maxDate),
        selectedParticipant?.participantId,
        undefined,
        {
          signal: dataAbortController.current.signal,
        },
      )
        .then((result) => {
          if (thisPromise === dataPromise.current) {
            if (!result?.data?.authorizations) {
              throw new Error("");
            }

            setError(null);

            let authorizations = result.data.authorizations;
            if (isScramblerEnabled) {
              authorizations = authorizations.map((authorization: DashboardAuthorization) => {
                authorization.startDate = getScrambledDateString(authorization.startDate);
                authorization.stopDate = getScrambledDateString(authorization.stopDate);
                authorization.claims = (authorization.claims ?? []).map((claim) => ({
                  ...claim,
                  serviceDate: getScrambledDateString(claim.serviceDate),
                }));

                return authorization;
              });
            }

            const theOptions = authorizations
              .map((authorization: DashboardAuthorization) => {
                const startDate = parseWallClockDate(authorization.startDate);
                const stopDate = parseWallClockDate(authorization.stopDate);

                return {
                  id: authorization.authorizationServiceId,
                  description: authorization.description,
                  status: authorization.status,
                  label: `${formatShortDate(startDate)}-${formatShortDate(stopDate)}:${authorization.description ? ` ${authorization.description}` : ""}`,
                  startDate,
                  stopDate,
                };
              })
              .sort((a: any, b: any) => {
                const statusComparison = a.status.localeCompare(b.status);
                return statusComparison === 0 ? b.label.localeCompare(a.label) : statusComparison;
              });

            setDashboardData({
              authorizations: authorizations,
              authorizationOptions: theOptions,
            });
          }
        })
        .catch(() => {
          setError("Failed to load dashboard data. Please try again later");
        })
        .finally(() => {
          if (thisPromise === dataPromise.current) {
            isInitialized.current = true;
            setIsLoading(false);
          }
        });

      dataPromise.current = thisPromise;
    }

    return () => {
      dataAbortController.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <DashboardPPTContext.Provider value={{ ...dashboardData, error, isLoading }}>{children}</DashboardPPTContext.Provider>;
};
